<template>
	<article class="d-selfservice-page-custom">
		<PageHeader
			use-wide-image
			v-bind="{
				...$attrs,
				...$props,
			}"
		/>

		<BlockList class="mt-5xl" :block-list="contentElements">
			<!-- Aside -->
			<template
				v-if="
					link ||
					(supportBox &&
						(supportBox.title ||
							supportBox.openingHours ||
							supportBox.phone))
				"
				#aside
			>
				<div
					:class="[
						'd-selfservice-page-custom__aside',
						'sticky top-6xl mb-md >=1024:mb-0 >=1024:max-w-3/12col',
					]"
				>
					<!-- Button -->
					<div
						v-if="link && link[0]"
						class="flex flex-col items-center >=1024:max-w-fit"
					>
						<NuxtLinkExt
							:to="link[0].url"
							:target="link[0].target"
							class="w-full >=1024:w-auto"
						>
							<BaseButton
								class="
									d-selfservice-page-custom__aside-button
									brea
								"
								tag="div"
							>
								<template #default>
									<span v-text="link[0].name"></span>
								</template>
								<template #icon>
									<SvgArrow class="transform -rotate-45" />
								</template>
							</BaseButton>
						</NuxtLinkExt>

						<span
							v-if="requiredNemId"
							:class="[
								'mt-2xs',
								'font-medium font-darker-grotesque',
								'text-mit-id text-text-placeholder',
							]"
							v-text="'Kræver MitID'"
						>
						</span>
					</div>

					<!-- Supporting info -->
					<div
						v-if="
							supportBox &&
							(supportBox.title ||
								supportBox.openingHours ||
								supportBox.phone)
						"
						:class="[
							'max-w-2/12col',

							{
								'hidden >=1024:block': true,
								'mt-2xl': link && link[0],
							},
						]"
					>
						<h2
							v-if="supportBox.title"
							:class="[
								'font-darker-grotesque font-extrabold text-aside-title uppercase',
							]"
							v-html="supportBox.title"
						></h2>

						<template
							v-for="(item, index) in [
								supportBox.phone && {
									label: supportBox.phoneLabel,
									value: supportBox.phone,
								},
								supportBox.openingHours && {
									label: supportBox.openingHoursLabel,
									value: supportBox.openingHours,
								},
							].filter(Boolean)"
						>
							<dt
								:key="`title-${index}`"
								class="
									font-darker-grotesque
									text-aside-title
									font-bold
									uppercase
								"
								:class="{
									'mt-lg': index || supportBox.title,
								}"
								v-html="item.label"
							></dt>
							<dd
								:key="`data-${index}`"
								class="
									font-darker-grotesque
									text-aside
									font-medium
									mt-6
								"
								v-html="item.value"
							></dd>
						</template>
					</div>
				</div>
			</template>

			<!-- Before -->
			<template #before>
				<div
					v-if="
						(thisIsWhatYouNeed && thisIsWhatYouNeed.length) ||
						(thisIsHowYouDo && thisIsHowYouDo.length)
					"
					class="s-rich-text"
					:class="
						contentElements && contentElements.length && 'mb-5xl'
					"
				>
					<div v-if="thisIsWhatYouNeed && thisIsWhatYouNeed.length">
						<BaseH3 tag="h2" v-text="'Du får brug for'" />

						<ul>
							<li
								v-for="(item, index) in thisIsWhatYouNeed"
								:key="`you-need-${index}`"
								v-text="item"
							></li>
						</ul>
					</div>

					<div
						v-if="thisIsHowYouDo && thisIsHowYouDo.length"
						class="mt-3xl"
					>
						<BaseH3 tag="h2" v-text="'Sådan gør du'" />

						<ol>
							<li
								v-for="(item, index) in thisIsHowYouDo"
								:key="`you-do-${index}`"
								v-text="item"
							></li>
						</ol>
					</div>
				</div>
			</template>

			<template #after>
				<!-- Supporting info -->
				<div
					v-if="
						supportBox &&
						(supportBox.title ||
							supportBox.openingHours ||
							supportBox.phone)
					"
					:class="{
						'>=1024:hidden': true,
						'mt-7xl': link && link[0],
					}"
				>
					<BaseH2
						v-if="supportBox.title"
						class="mb-xl"
						v-html="supportBox.title"
					/>

					<dl
						class="grid grid-cols-3 gap-x-layout-gutter"
						style="grid-template-columns: auto auto 1fr !important"
					>
						<template
							v-for="(item, index) in [
								supportBox.phone && {
									label: supportBox.phoneLabel,
									value: supportBox.phone,
								},
								supportBox.openingHours && {
									label: supportBox.openingHoursLabel,
									value: supportBox.openingHours,
								},
							].filter(Boolean)"
						>
							<dt
								:key="`title-${index}`"
								class="
									row-start-1
									font-darker-grotesque
									text-aside-title
									font-bold
									uppercase
								"
								:class="index ? 'col-start-2' : 'col-start-1'"
								v-html="item.label"
							></dt>
							<dd
								:key="`data-${index}`"
								class="
									row-start-2
									font-darker-grotesque
									text-aside
									font-medium
								"
								:class="index ? 'col-start-2' : 'col-start-1'"
								v-html="item.value"
							></dd>
						</template>
					</dl>
				</div>
			</template>
		</BlockList>

		<!-- Related pages -->
		<div
			v-if="
				(showRelatedPages &&
					showRelatedPages.pages &&
					showRelatedPages.pages.length) ||
				(showMorePages &&
					showMorePages.pages &&
					showMorePages.pages.length)
			"
			class="mt-5xl space-y-5xl"
		>
			<RelatedPages
				v-if="
					showRelatedPages &&
					showRelatedPages.pages &&
					showRelatedPages.pages.length
				"
				v-bind="{ ...showRelatedPages, links: showRelatedPages.pages }"
			/>

			<RelatedPages
				v-if="
					showMorePages &&
					showMorePages.pages &&
					showMorePages.pages.length
				"
				v-bind="{ ...showMorePages, links: showMorePages.pages }"
				grey
			/>
		</div>

		<FeedbackModule
			v-if="!hideFeedback"
			:feedback="feedback"
			class="mt-7xl"
		/>
	</article>
</template>

<script>
import SvgArrow from '~/assets/svgs/icon-arrow.svg?inline';
import PageHeader from '~/components/main/PageHeader';
import BlockList from '~/components/main/BlockList';
import RelatedPages from '~/components/shared/RelatedPages';
import FeedbackModule from '~/components/shared/FeedbackModule';

export default {
	name: 'MainSelfservicePageCustom',
	components: {
		SvgArrow,
		PageHeader,
		BlockList,
		RelatedPages,
		FeedbackModule,
	},

	inheritAttrs: false,

	props: {
		contentElements: {
			type: Array,
			default: () => [],
		},
		hideFeedback: {
			type: Boolean,
			default: false,
		},
		feedback: Object,
		title: String,
		link: Array,
		requiredNemId: Boolean,
		supportBox: Object,
		thisIsWhatYouNeed: Array,
		thisIsHowYouDo: Array,

		showRelatedPages: Object,
		showMorePages: Object,
	},
};
</script>

<style lang="postcss">
/* .d-selfservice-page-custom__aside .c-base-button__inner {
	@apply min-w-max;
} */

@screen <1024 {
	.d-selfservice-page-custom__aside .c-base-button__inner {
		@apply justify-center py-md;
	}
}
</style>
